
import React from 'react';
import { TabsContent } from "@/components/ui/tabs";
import { Tables } from "@/integrations/supabase/types";
import QuoteCard from "./QuoteCard";
import EmptyQuotesState from "./EmptyQuotesState";

type Quote = Tables<'quotes'>;

interface QuoteTabContentProps {
  value: string;
  quotes: Quote[];
  isAdmin: boolean;
  canInteractWithQuotes: boolean;
  isAdminViewingOthersBooking: boolean;
  loadingQuoteId: string | null;
  generatingPayment: string | null;
  onQuoteResponse: (quoteId: string, action: 'accepted' | 'rejected', response?: string) => void;
  onDeleteQuote: (quoteId: string) => void;
  onReviseQuote?: (quote: Quote) => void;
  onAddVariation?: (quote: Quote) => void;
  onPaymentUpdate?: () => void;
  emptyMessage: string;
  showDescription?: boolean;
  bookingId?: string;
  showEmptyState?: boolean;
}

const QuoteTabContent = ({
  value,
  quotes,
  isAdmin,
  canInteractWithQuotes,
  isAdminViewingOthersBooking,
  loadingQuoteId,
  generatingPayment,
  onQuoteResponse,
  onDeleteQuote,
  onReviseQuote,
  onAddVariation,
  onPaymentUpdate,
  emptyMessage,
  showDescription = false,
  bookingId,
  showEmptyState = false
}: QuoteTabContentProps) => {
  // Sort quotes to show parent quotes first, then children
  const sortedQuotes = [...quotes].sort((a, b) => {
    // If both are parent quotes or both are child quotes, sort by creation date
    if ((!a.parent_quote_id && !b.parent_quote_id) || (a.parent_quote_id && b.parent_quote_id)) {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    // Parent quotes come before child quotes
    if (!a.parent_quote_id && b.parent_quote_id) return -1;
    if (a.parent_quote_id && !b.parent_quote_id) return 1;
    return 0;
  });

  // Group quotes with their children
  const groupedQuotes = [];
  const processedQuoteIds = new Set<string>();

  // First, process all parent quotes (quotes without parent_quote_id)
  const parentQuotes = sortedQuotes.filter(quote => !quote.parent_quote_id);
  
  parentQuotes.forEach(parentQuote => {
    if (processedQuoteIds.has(parentQuote.id)) return;
    
    const children = sortedQuotes.filter(q => q.parent_quote_id === parentQuote.id);
    
    groupedQuotes.push({
      parent: parentQuote,
      children: children
    });
    
    // Mark this parent and all its children as processed
    processedQuoteIds.add(parentQuote.id);
    children.forEach(child => processedQuoteIds.add(child.id));
  });

  // Then, process any remaining child quotes that might not have valid parents
  const orphanedChildren = sortedQuotes.filter(quote => 
    quote.parent_quote_id && !processedQuoteIds.has(quote.id)
  );
  
  orphanedChildren.forEach(orphanedChild => {
    groupedQuotes.push({
      parent: orphanedChild,
      children: []
    });
    processedQuoteIds.add(orphanedChild.id);
  });

  return (
    <TabsContent value={value} className="mt-4">
      {quotes.length === 0 ? (
        showEmptyState && bookingId ? (
          <EmptyQuotesState 
            isAdmin={isAdmin} 
            bookingId={bookingId} 
            onQuoteUpdate={onPaymentUpdate} 
          />
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            {emptyMessage}
          </div>
        )
      ) : (
        <div className="space-y-4">
          {showDescription && (
            <p className="text-sm text-muted-foreground mb-4">
              Complete chronological history of all quotes for this booking, including revisions and variations.
            </p>
          )}
          
          {groupedQuotes.map(({ parent, children }) => (
            <div key={parent.id} className="space-y-3">
              <QuoteCard
                quote={parent}
                canInteractWithQuotes={canInteractWithQuotes}
                isAdminViewingOthersBooking={isAdminViewingOthersBooking}
                loadingQuoteId={loadingQuoteId}
                generatingPayment={generatingPayment}
                onQuoteResponse={onQuoteResponse}
                onDeleteQuote={onDeleteQuote}
                onReviseQuote={onReviseQuote}
                onAddVariation={onAddVariation}
                onPaymentUpdate={onPaymentUpdate}
              />
              
              {children.map((childQuote) => (
                <QuoteCard
                  key={childQuote.id}
                  quote={childQuote}
                  isChild={true}
                  canInteractWithQuotes={canInteractWithQuotes}
                  isAdminViewingOthersBooking={isAdminViewingOthersBooking}
                  loadingQuoteId={loadingQuoteId}
                  generatingPayment={generatingPayment}
                  onQuoteResponse={onQuoteResponse}
                  onDeleteQuote={onDeleteQuote}
                  onReviseQuote={onReviseQuote}
                  onAddVariation={onAddVariation}
                  onPaymentUpdate={onPaymentUpdate}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </TabsContent>
  );
};

export default QuoteTabContent;
